import { Line as ZeroLine } from '@visx/shape';
import { type CSSProperties, type MutableRefObject } from 'react';

import {
  type MaskingProps,
  useCurrentTheme,
} from '@dynatrace/strato-components/core';

import { ContextValueIndicator } from './components/ContextValueIndicator.js';
import {
  AXIS_HEIGHT,
  CONTEXT_VALUE_MAX_WIDTH,
  CONTEXT_VALUES_HEIGHT_OFFSET,
  CONTEXT_VALUES_INDICATOR_LEFT_OFFSET,
  DEFAULT_SCALE_FALLBACK_COLOR,
  DEFAULT_SHOW_CONTEXT_VALUES,
  SPARKLINE_ARIA_LABEL,
} from './constants.js';
import { SparklineRenderer } from './SparklineRenderer.js';
import type { SparklineProps } from './types/sparkline-props.js';
import type { SparklineThresholdProps } from './types/sparkline-thresholds-props.js';
import { SparklineXAxisInternalProps } from './types/sparkline-x-axis-props.js';
import type { SparklineYAxisProps } from './types/sparkline-y-axis-props.js';
import { calculateArcRadius } from './utils/arc-radius.js';
import { getContextValueWidth } from './utils/get-context-value-width.js';
import { getShowContextValues } from './utils/get-show-context-values.js';
import { getSparklineHeight } from './utils/get-sparkline-height.js';
import { getSparklineXScale } from './utils/get-sparkline-x-scale.js';
import { getSparklineYScale } from './utils/get-sparkline-y-scale.js';
import { sanitizeSparklineData } from './utils/sanitize-sparkline-data.js';
import { calculateStrokeWidth } from './utils/stroke-width.js';
import { mapToThresholdsIndicators } from './utils/threshold-indicators.js';
import { useObserveContainerSize } from '../../hooks/useObserveContainerSize.js';
import type { Timeseries } from '../../types/timeseries.js';
import { defaultTickValueFormatter } from '../../utils/format-value.js';
import { getFormattedTicks } from '../../utils/formatter-by-ticks.js';
import { getValidColor } from '../../utils/getValidColor.js';
import { getHorizontalNumTicks } from '../../utils/ticks.js';
import { TimeBottomAxis } from '../axes/components/TimeBottomAxis.js';
import { Canvas } from '../canvas/Canvas.js';
import { CanvasContext } from '../canvas/CanvasContext.js';
import type { CanvasShape } from '../canvas/types/canvas-shape.js';
import { createCanvasGradient } from '../canvas/utils/create-canvas-gradient.js';
import {
  DEFAULT_STROKE_COLOR,
  DEFAULT_STROKE_WIDTH,
} from '../grid/GridLine.js';
import { ThresholdIndicator } from '../threshold-bar/indicators/ThresholdIndicator.js';

export interface SparklineDisplayProps extends SparklineProps, MaskingProps {
  data: Timeseries;
  thresholds?: SparklineThresholdProps[];
  xAxis: SparklineXAxisInternalProps;
  yAxis: SparklineYAxisProps;
  style?: CSSProperties;
}

/**
 * SparklineContainer component
 */
export const SparklineContainer = (props: SparklineDisplayProps) => {
  const {
    data,
    color,
    variant = 'line',
    showTicks = false,
    thresholds = [],
    xAxis,
    yAxis,
    gapPolicy = 'connect',
    showContextValues = DEFAULT_SHOW_CONTEXT_VALUES,
    curve,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    style,
  } = props;

  const [containerDimension, sizeObserverRef] = useObserveContainerSize();

  const theme = useCurrentTheme();

  const shouldShowTicks = showTicks && containerDimension.height >= 40;

  const { width: containerWidth } = containerDimension;

  const { sanitizedSeries, domainValues } = sanitizeSparklineData(
    data,
    gapPolicy,
    xAxis,
  );

  const { showContextMin, showContextMax } = showContextValues
    ? getShowContextValues(yAxis, domainValues)
    : { showContextMin: false, showContextMax: false };

  const height = getSparklineHeight(
    containerDimension.height,
    shouldShowTicks,
    showContextMin,
    showContextMax,
  );

  const formattedContextMaxValue = defaultTickValueFormatter(domainValues.max);
  const formattedContextMinValue = defaultTickValueFormatter(domainValues.min);

  const maxContextValueWidth = showContextValues
    ? Math.min(
        getContextValueWidth(formattedContextMaxValue),
        CONTEXT_VALUE_MAX_WIDTH,
      )
    : 0;

  const minContextValueWidth = showContextValues
    ? Math.min(
        getContextValueWidth(formattedContextMinValue),
        CONTEXT_VALUE_MAX_WIDTH,
      )
    : 0;

  const contextValueWidth =
    Math.max(maxContextValueWidth, minContextValueWidth) +
    CONTEXT_VALUES_INDICATOR_LEFT_OFFSET;

  const chartWidth = showContextValues
    ? containerWidth - contextValueWidth
    : containerWidth;

  const strokeWidth = calculateStrokeWidth(height, chartWidth);
  const arcRadius = calculateArcRadius(height, chartWidth);

  const isBarVariant = variant === 'bar';

  const xScale = getSparklineXScale(
    sanitizedSeries,
    chartWidth,
    strokeWidth / 2,
    isBarVariant,
  );

  const horizontalNumTicks = getHorizontalNumTicks(chartWidth);
  const { ticks, formattedTicks } = getFormattedTicks(
    horizontalNumTicks,
    xScale,
    undefined,
    undefined,
  );

  const yScale = getSparklineYScale(domainValues, height, yAxis, isBarVariant);

  const thresholdIndicators = mapToThresholdsIndicators(
    thresholds,
    chartWidth,
    height,
    xScale,
    yScale,
  );

  const validatedColor = getValidColor(color, DEFAULT_SCALE_FALLBACK_COLOR);

  const [min, max] = yScale.domain();
  const displayZeroLine = min <= 0 && max >= 0;
  const zeroLineYPos = yScale(0) ?? -1;

  const domainValuesYOffset = showContextMax ? CONTEXT_VALUES_HEIGHT_OFFSET : 0;
  const chartWidthPadding = showContextValues
    ? maxContextValueWidth + CONTEXT_VALUES_INDICATOR_LEFT_OFFSET
    : 0;

  return (
    <div
      style={{ height: '100%' }}
      ref={sizeObserverRef}
      data-testid="sparkline"
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-label={SPARKLINE_ARIA_LABEL}
      role="img"
    >
      <div
        style={{
          transform: `translate(${chartWidthPadding}px,${
            showContextMax ? CONTEXT_VALUES_HEIGHT_OFFSET : 0
          }px)`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <svg
          width={chartWidth}
          height={height}
          style={{ position: 'absolute' }}
        >
          {displayZeroLine && (
            <ZeroLine
              from={{ x: 0, y: zeroLineYPos }}
              to={{ x: chartWidth, y: zeroLineYPos }}
              stroke={DEFAULT_STROKE_COLOR}
              strokeWidth={DEFAULT_STROKE_WIDTH}
              data-testid="sparkline-zero-baseline"
            />
          )}
          {thresholdIndicators.length &&
            thresholdIndicators.map(
              ({ id, indicatorData, color, strokeOnly }) => (
                <ThresholdIndicator
                  key={`threshold-indicator-${id}`}
                  data={indicatorData}
                  color={color}
                  strokeOnly={strokeOnly}
                  isActive={false}
                />
              ),
            )}
        </svg>
        <div style={{ position: 'absolute', ...style }}>
          <CanvasContext.Provider
            value={{
              shapeRefs: new Map<string, MutableRefObject<CanvasShape>>(),
            }}
          >
            <Canvas width={containerWidth} height={height}>
              <SparklineRenderer
                data={sanitizedSeries.datapoints}
                width={chartWidth}
                height={height}
                xScale={xScale}
                yScale={yScale}
                strokeColor={validatedColor}
                fillColor={createCanvasGradient({
                  height,
                  color: validatedColor,
                  domain: yScale.domain(),
                  theme,
                })}
                variant={variant}
                strokeWidth={strokeWidth}
                radius={arcRadius}
                curve={curve}
                gapPolicy={gapPolicy}
              />
            </Canvas>
          </CanvasContext.Provider>
          {shouldShowTicks && (
            <TimeBottomAxis
              ticks={ticks}
              formattedTicks={formattedTicks}
              scale={xScale}
              containerHeight={AXIS_HEIGHT}
              containerWidth={chartWidth}
            />
          )}
        </div>
      </div>
      {showContextMin && (
        <ContextValueIndicator
          formattedValue={formattedContextMinValue}
          width={containerWidth}
          stringWidth={minContextValueWidth}
          yPosition={yScale(domainValues.min) + domainValuesYOffset}
        />
      )}
      {showContextMax && (
        <ContextValueIndicator
          formattedValue={formattedContextMaxValue}
          width={containerWidth}
          stringWidth={maxContextValueWidth}
          yPosition={yScale(domainValues.max) + domainValuesYOffset}
        />
      )}
    </div>
  );
};

SparklineContainer['displayName'] = 'SparklineContainer';
